import { useState, useCallback, useEffect } from 'react';
import { parseGS1, GS1Data } from '../utils/gs1Parser';
import { SAMPLE_BARCODE_CASES } from '../data/sampleBarcodes';
import type {
  ValidationPhoto,
  ValidationExpectedData,
  ValidationFieldResult,
  ValidationCase,
} from '../types/barcodeValidation';

const STORAGE_KEY = 'logmed_barcode_validation_cases';

const emptyExpected: ValidationExpectedData = {
  gtin: '',
  lote: '',
  validade: '',
  serie: '',
  dataProducao: '',
  melhorUso: '',
  quantidade: '',
  refCode: '',
};

function formatDate(d: Date | null): string | null {
  if (!d) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function resizeImage(file: File, maxSize: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context not available'));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useBarcodeValidation() {
  const [rawBarcode, setRawBarcode] = useState('');
  const [parsedData, setParsedData] = useState<GS1Data | null>(null);
  const [expectedData, setExpectedData] = useState<ValidationExpectedData>({ ...emptyExpected });
  const [photos, setPhotos] = useState<ValidationPhoto[]>([]);
  const [savedCases, setSavedCases] = useState<ValidationCase[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationFieldResult[] | null>(null);
  const [currentCaseIndex, setCurrentCaseIndex] = useState<number>(-1);

  // Load saved cases from localStorage on mount, fallback to samples
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.length > 0) {
          setSavedCases(parsed);
          return;
        }
      }
    } catch { /* ignore */ }
    // No saved cases — load samples
    setSavedCases(SAMPLE_BARCODE_CASES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(SAMPLE_BARCODE_CASES));
  }, []);

  const persistCases = useCallback((cases: ValidationCase[]) => {
    setSavedCases(cases);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cases));
  }, []);

  const parseBarcode = useCallback((raw: string) => {
    if (!raw.trim()) {
      setParsedData(null);
      setValidationResults(null);
      return;
    }
    const result = parseGS1(raw);
    setParsedData(result);
    setValidationResults(null);
  }, []);

  const runValidation = useCallback((): ValidationFieldResult[] | null => {
    if (!parsedData) return null;

    const fields: { field: keyof ValidationExpectedData; label: string; getValue: () => string | null }[] = [
      { field: 'gtin', label: 'GTIN', getValue: () => parsedData.gtin },
      { field: 'lote', label: 'Lote', getValue: () => parsedData.lote },
      { field: 'validade', label: 'Validade', getValue: () => formatDate(parsedData.validade) },
      { field: 'serie', label: 'Serial', getValue: () => parsedData.serie },
      { field: 'dataProducao', label: 'Data Producao', getValue: () => formatDate(parsedData.dataProducao) },
      { field: 'melhorUso', label: 'Melhor Uso', getValue: () => formatDate(parsedData.melhorUso) },
      { field: 'quantidade', label: 'Quantidade', getValue: () => parsedData.quantidade?.toString() ?? null },
      { field: 'refCode', label: 'Ref Code (AI 240)', getValue: () => parsedData.refCode },
    ];

    const results: ValidationFieldResult[] = fields.map(({ field, label, getValue }) => {
      const parsedValue = getValue();
      const expectedValue = expectedData[field] || null;
      let match: boolean | null = null;

      if (expectedValue && parsedValue) {
        match = parsedValue.toLowerCase().trim() === expectedValue.toLowerCase().trim();
      } else if (expectedValue && !parsedValue) {
        match = false;
      }

      return { field, label, parsedValue, expectedValue, match };
    });

    setValidationResults(results);
    return results;
  }, [parsedData, expectedData]);

  const addPhoto = useCallback(async (file: File) => {
    const dataUrl = await resizeImage(file, 800);
    const photo: ValidationPhoto = {
      id: crypto.randomUUID(),
      dataUrl,
      name: file.name,
      addedAt: new Date().toISOString(),
    };
    setPhotos((prev) => [...prev, photo]);
  }, []);

  const removePhoto = useCallback((id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updateExpectedField = useCallback((field: keyof ValidationExpectedData, value: string) => {
    setExpectedData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const clearExpected = useCallback(() => {
    setExpectedData({ ...emptyExpected });
    setValidationResults(null);
  }, []);

  const saveCase = useCallback((name: string) => {
    const newCase: ValidationCase = {
      id: crypto.randomUUID(),
      name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      photos,
      rawBarcode,
      expectedData,
    };
    persistCases([newCase, ...savedCases]);
  }, [photos, rawBarcode, expectedData, savedCases, persistCases]);

  const _loadCaseData = useCallback((found: ValidationCase) => {
    setRawBarcode(found.rawBarcode);
    setExpectedData(found.expectedData);
    setPhotos(found.photos);
    if (found.rawBarcode) {
      const result = parseGS1(found.rawBarcode);
      setParsedData(result);
    } else {
      setParsedData(null);
    }
    setValidationResults(null);
  }, []);

  const loadCase = useCallback((id: string) => {
    const idx = savedCases.findIndex((c) => c.id === id);
    if (idx === -1) return;
    setCurrentCaseIndex(idx);
    _loadCaseData(savedCases[idx]);
  }, [savedCases, _loadCaseData]);

  const navigateCase = useCallback((direction: 'prev' | 'next') => {
    if (savedCases.length === 0) return;
    let newIdx: number;
    if (currentCaseIndex === -1) {
      newIdx = direction === 'next' ? 0 : savedCases.length - 1;
    } else {
      newIdx = direction === 'next'
        ? (currentCaseIndex + 1) % savedCases.length
        : (currentCaseIndex - 1 + savedCases.length) % savedCases.length;
    }
    setCurrentCaseIndex(newIdx);
    _loadCaseData(savedCases[newIdx]);
  }, [savedCases, currentCaseIndex, _loadCaseData]);

  const deleteCase = useCallback((id: string) => {
    const newCases = savedCases.filter((c) => c.id !== id);
    persistCases(newCases);
    if (currentCaseIndex >= newCases.length) {
      setCurrentCaseIndex(newCases.length - 1);
    }
  }, [savedCases, persistCases, currentCaseIndex]);

  const loadSamples = useCallback(() => {
    persistCases(SAMPLE_BARCODE_CASES);
    setCurrentCaseIndex(0);
    _loadCaseData(SAMPLE_BARCODE_CASES[0]);
  }, [persistCases, _loadCaseData]);

  const clearAll = useCallback(() => {
    setRawBarcode('');
    setParsedData(null);
    setExpectedData({ ...emptyExpected });
    setPhotos([]);
    setValidationResults(null);
    setCurrentCaseIndex(-1);
  }, []);

  return {
    rawBarcode,
    setRawBarcode,
    parsedData,
    expectedData,
    photos,
    savedCases,
    validationResults,
    currentCaseIndex,
    parseBarcode,
    runValidation,
    addPhoto,
    removePhoto,
    updateExpectedField,
    clearExpected,
    saveCase,
    loadCase,
    navigateCase,
    deleteCase,
    loadSamples,
    clearAll,
  };
}
